import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Search, Download, FileText, Building2 } from 'lucide-react';
import api from '../api';
import { Company, PaginatedResult } from '../types';
import Pagination from '../components/Pagination';

const formatDate = (dateStr: string): string =>
  new Date(dateStr).toLocaleDateString('fr-FR');

const CompliancePage: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('optout');

  // Opt-out list
  const [optOutCompanies, setOptOutCompanies] = useState<Company[]>([]);
  const [optOutTotal, setOptOutTotal] = useState(0);
  const [optOutPage, setOptOutPage] = useState(1);
  const [optOutLoading, setOptOutLoading] = useState(true);
  const [optOutSearch, setOptOutSearch] = useState('');

  // Retention stats
  const [retentionStats, setRetentionStats] = useState<any>(null);
  const [retentionLoading, setRetentionLoading] = useState(false);

  // Notice template
  const [noticeCompanyName, setNoticeCompanyName] = useState('');
  const [noticeContactName, setNoticeContactName] = useState('');
  const [generatedNotice, setGeneratedNotice] = useState('');

  const fetchOptOutCompanies = useCallback(async () => {
    setOptOutLoading(true);
    try {
      const params: any = { page: optOutPage, limit: 25, optOut: true };
      if (optOutSearch) params.search = optOutSearch;
      const res = await api.get<PaginatedResult<Company>>('/companies', { params });
      setOptOutCompanies(res.data.data || []);
      setOptOutTotal(res.data.total || 0);
    } catch (err) {
      // ignore
    } finally {
      setOptOutLoading(false);
    }
  }, [optOutPage, optOutSearch]);

  const fetchRetentionStats = useCallback(async () => {
    setRetentionLoading(true);
    try {
      const res = await api.get('/compliance/retention-stats');
      setRetentionStats(res.data);
    } catch (err) {
      // provide fallback stats
      setRetentionStats(null);
    } finally {
      setRetentionLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'optout') {
      fetchOptOutCompanies();
    } else if (activeTab === 'retention') {
      fetchRetentionStats();
    }
  }, [activeTab, fetchOptOutCompanies, fetchRetentionStats]);

  const handleToggleOptOut = async (company: Company) => {
    try {
      await api.patch(`/companies/${company.id}`, { optOut: !company.optOut });
      fetchOptOutCompanies();
    } catch (err) {
      // ignore
    }
  };

  const generateNotice = () => {
    const companyName = noticeCompanyName || '[Nom de l\'entreprise]';
    const contactName = noticeContactName || '[Nom du contact]';
    const today = new Date().toLocaleDateString('fr-FR');

    const notice = `NOTIFICATION DE COLLECTE DE DONNÉES

Date : ${today}

Destinataire : ${contactName}
Entreprise : ${companyName}

Objet : Information sur le traitement de vos données personnelles

Madame, Monsieur,

Conformément au Règlement Général sur la Protection des Données (RGPD - Règlement UE 2016/679), nous vous informons que nous traitons certaines de vos données à caractère personnel dans le cadre de notre activité commerciale.

1. RESPONSABLE DU TRAITEMENT
Le responsable du traitement est la société exploitant Industrial CRM.

2. DONNÉES COLLECTÉES
Les données collectées comprennent :
- Nom, prénom, fonction
- Coordonnées professionnelles (email, téléphone)
- Informations relatives à votre entreprise (SIREN, adresse, secteur d'activité)

3. FINALITÉ DU TRAITEMENT
Ces données sont utilisées dans le cadre de notre prospection commerciale B2B, conformément à notre intérêt légitime.

4. DURÉE DE CONSERVATION
Les données sont conservées pendant une durée maximale de 3 ans à compter du dernier contact commercial, conformément aux recommandations de la CNIL.

5. VOS DROITS
Vous disposez d'un droit d'accès, de rectification, d'effacement, de limitation du traitement, de portabilité des données et d'opposition.

Pour exercer ces droits, vous pouvez nous contacter par email ou par courrier.

6. OPT-OUT
Vous pouvez à tout moment demander à ne plus être contacté en exerçant votre droit d'opposition.

Cordialement,
L'équipe commerciale`;

    setGeneratedNotice(notice);
  };

  const handleCopyNotice = () => {
    navigator.clipboard.writeText(generatedNotice);
  };

  const tabs = [
    { key: 'optout', label: 'Liste opt-out' },
    { key: 'retention', label: 'Rétention des données' },
    { key: 'notice', label: 'Modèle de notification' },
  ];

  const optOutTotalPages = Math.ceil(optOutTotal / 25);

  return (
    <div>
      <div className="page-header">
        <h1>
          <span className="inline-flex"><Shield size={28} /> Conformité RGPD</span>
        </h1>
      </div>

      <div className="tabs">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            className={`tab ${activeTab === tab.key ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Opt-out Tab */}
      {activeTab === 'optout' && (
        <div className="card">
          <div className="card-header">
            <h3>Entreprises en opt-out</h3>
            <span className="badge badge-danger">{optOutTotal} entreprise{optOutTotal > 1 ? 's' : ''}</span>
          </div>
          <div className="card-body">
            <div className="toolbar mb-3">
              <form onSubmit={(e) => { e.preventDefault(); setOptOutPage(1); fetchOptOutCompanies(); }} className="search-bar">
                <Search />
                <input
                  type="text"
                  placeholder="Rechercher..."
                  value={optOutSearch}
                  onChange={(e) => setOptOutSearch(e.target.value)}
                />
              </form>
            </div>

            {optOutLoading ? (
              <div className="spinner-container"><div className="spinner" /></div>
            ) : optOutCompanies.length === 0 ? (
              <div className="empty-state">
                <Building2 size={48} />
                <p className="empty-state-title">Aucune entreprise en opt-out</p>
                <p className="empty-state-description">Toutes les entreprises sont contactables.</p>
              </div>
            ) : (
              <>
                <div className="table-container">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Entreprise</th>
                        <th>SIREN</th>
                        <th>Ville</th>
                        <th>Date de collecte</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {optOutCompanies.map((company) => (
                        <tr key={company.id}>
                          <td>
                            <span
                              className="text-primary cursor-pointer font-semibold"
                              onClick={() => navigate(`/companies/${company.id}`)}
                            >
                              {company.name}
                            </span>
                          </td>
                          <td>{company.siren}</td>
                          <td>{company.addressCity || '-'}</td>
                          <td>{formatDate(company.createdAt)}</td>
                          <td>
                            <button
                              className="btn btn-sm btn-success"
                              onClick={() => handleToggleOptOut(company)}
                            >
                              Retirer l'opt-out
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <Pagination page={optOutPage} totalPages={optOutTotalPages} onPageChange={setOptOutPage} total={optOutTotal} />
              </>
            )}
          </div>
        </div>
      )}

      {/* Retention Tab */}
      {activeTab === 'retention' && (
        <div className="card">
          <div className="card-header"><h3>Rapport de rétention des données</h3></div>
          <div className="card-body">
            {retentionLoading ? (
              <div className="spinner-container"><div className="spinner" /></div>
            ) : retentionStats ? (
              <div>
                <div className="kpi-grid mb-4">
                  <div className="kpi-card">
                    <div className="kpi-value">{retentionStats.totalCompanies?.toLocaleString('fr-FR') || 0}</div>
                    <div className="kpi-label">Total entreprises</div>
                  </div>
                  <div className="kpi-card">
                    <div className="kpi-value">{retentionStats.totalContacts?.toLocaleString('fr-FR') || 0}</div>
                    <div className="kpi-label">Total contacts</div>
                  </div>
                  <div className="kpi-card">
                    <div className="kpi-value">{retentionStats.expiredRecords?.toLocaleString('fr-FR') || 0}</div>
                    <div className="kpi-label">Données expirées</div>
                  </div>
                  <div className="kpi-card">
                    <div className="kpi-value">{retentionStats.optOutCount?.toLocaleString('fr-FR') || 0}</div>
                    <div className="kpi-label">Opt-out</div>
                  </div>
                </div>

                {retentionStats.retentionPolicy && (
                  <div className="card" style={{ background: '#F9FAFB' }}>
                    <div className="card-body">
                      <h3 className="mb-2">Politique de rétention</h3>
                      <p className="text-secondary" style={{ lineHeight: 1.8 }}>
                        {retentionStats.retentionPolicy}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div>
                <div className="kpi-grid mb-4">
                  <div className="kpi-card">
                    <div className="kpi-value">-</div>
                    <div className="kpi-label">Total entreprises</div>
                  </div>
                  <div className="kpi-card">
                    <div className="kpi-value">-</div>
                    <div className="kpi-label">Total contacts</div>
                  </div>
                  <div className="kpi-card">
                    <div className="kpi-value">-</div>
                    <div className="kpi-label">Données expirées</div>
                  </div>
                  <div className="kpi-card">
                    <div className="kpi-value">-</div>
                    <div className="kpi-label">Opt-out</div>
                  </div>
                </div>

                <div className="card" style={{ background: '#F9FAFB' }}>
                  <div className="card-body">
                    <h3 className="mb-2">Politique de rétention</h3>
                    <p className="text-secondary" style={{ lineHeight: 1.8 }}>
                      Conformément aux recommandations de la CNIL pour la prospection B2B, les données personnelles
                      des contacts sont conservées pendant une durée maximale de 3 ans à compter du dernier contact
                      commercial. Les données d'entreprise (personnes morales) ne sont pas soumises au RGPD mais sont
                      conservées tant qu'elles sont pertinentes pour l'activité commerciale. Les données des personnes
                      ayant exercé leur droit d'opposition (opt-out) sont conservées uniquement dans une liste de blocage.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Notice Template Tab */}
      {activeTab === 'notice' && (
        <div className="card">
          <div className="card-header"><h3>Générateur de notification RGPD</h3></div>
          <div className="card-body">
            <div className="form-row mb-3">
              <div className="form-group">
                <label className="form-label">Nom de l'entreprise</label>
                <input
                  className="form-input"
                  value={noticeCompanyName}
                  onChange={(e) => setNoticeCompanyName(e.target.value)}
                  placeholder="Nom de l'entreprise"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Nom du contact</label>
                <input
                  className="form-input"
                  value={noticeContactName}
                  onChange={(e) => setNoticeContactName(e.target.value)}
                  placeholder="Nom du contact"
                />
              </div>
            </div>

            <div className="btn-group mb-3">
              <button className="btn btn-primary" onClick={generateNotice}>
                <FileText size={16} /> Générer la notification
              </button>
              {generatedNotice && (
                <button className="btn" onClick={handleCopyNotice}>
                  <Download size={16} /> Copier dans le presse-papier
                </button>
              )}
            </div>

            {generatedNotice && (
              <div style={{
                background: '#F9FAFB',
                border: '1px solid var(--border)',
                borderRadius: '8px',
                padding: '20px',
                whiteSpace: 'pre-wrap',
                fontSize: '0.85rem',
                lineHeight: 1.8,
                fontFamily: 'var(--font-sans)',
                maxHeight: '500px',
                overflow: 'auto',
              }}>
                {generatedNotice}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CompliancePage;
